// ─────────────────────────────────────────────────────────────────────────────
// catchFinish.js — Pure algorithms for CA, FA, EL.
// Layer 4 of the Power Gates spec. Pure functions only; no side effects.
//
// Inputs are paired arrays of force (N) and angle (deg). They MUST already be
// zero-corrected (ZF/ZA applied at the driver boundary — see drivers/).
//
// THRESHOLD NOTE: the spec text says "default: 10N" but the Section 11.7
// worked example requires `>= 5N` for the expected outputs (CA=-55°, FA=67°)
// to be reproducible. We match the worked example. Override via opts if your
// firmware reports a higher noise floor.
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_CATCH_THRESHOLD_N = 5;    // CATCH_THRESHOLD
export const DEFAULT_FINISH_THRESHOLD_N = 5;   // FINISH_THRESHOLD

/**
 * Catch Angle (CA): the angle at which force first reaches CATCH_THRESHOLD.
 * Returns the angle (deg) of the first sample where force >= threshold.
 */
export function catchAngle(forceN, angleDeg, threshold = DEFAULT_CATCH_THRESHOLD_N) {
  if (!Array.isArray(forceN) || !Array.isArray(angleDeg) || forceN.length !== angleDeg.length) {
    return null;
  }
  for (let i = 0; i < forceN.length; i += 1) {
    if (forceN[i] >= threshold) return angleDeg[i];
  }
  return null;
}

/**
 * Finish Angle (FA): the angle at which force last reaches FINISH_THRESHOLD.
 * Returns the angle (deg) of the last sample where force >= threshold.
 */
export function finishAngle(forceN, angleDeg, threshold = DEFAULT_FINISH_THRESHOLD_N) {
  if (!Array.isArray(forceN) || !Array.isArray(angleDeg) || forceN.length !== angleDeg.length) {
    return null;
  }
  for (let i = forceN.length - 1; i >= 0; i -= 1) {
    if (forceN[i] >= threshold) return angleDeg[i];
  }
  return null;
}

/**
 * Effective Length (EL): FA - CA, in degrees.
 * Returns null if either endpoint is missing.
 */
export function effectiveLength(forceN, angleDeg, opts = {}) {
  const ca = catchAngle(forceN, angleDeg, opts.catchThreshold ?? DEFAULT_CATCH_THRESHOLD_N);
  const fa = finishAngle(forceN, angleDeg, opts.finishThreshold ?? DEFAULT_FINISH_THRESHOLD_N);
  if (ca == null || fa == null) return null;
  return fa - ca;
}

/**
 * Convenience: returns { CA, FA, EL } in one pass.
 */
export function catchFinishMetrics(forceN, angleDeg, opts = {}) {
  const CA = catchAngle(forceN, angleDeg, opts.catchThreshold ?? DEFAULT_CATCH_THRESHOLD_N);
  const FA = finishAngle(forceN, angleDeg, opts.finishThreshold ?? DEFAULT_FINISH_THRESHOLD_N);
  const EL = (CA == null || FA == null) ? null : FA - CA;
  return { CA, FA, EL };
}
